import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  Theme,
  Typography,
  Popper,
  Fade,
  Paper,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SideComponent from "../SideComponent";
import { Close, East } from "@mui/icons-material";
import Wave from "../../Assets/images/wave.png";
import Me from "../../Assets/images/me.jpg";
import AboutLine from "../../Assets/Icons/AboutLine";
import LogoSvg from "../../Assets/Icons/logo.svg";

export const drawerWidth = 64;

const Header = ({ open, setOpen }: { open: boolean; setOpen: Function }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileRightOpen, setMobileRightOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRightDrawerToggle = () => {
    setMobileRightOpen(!mobileRightOpen);
  };

  const handleAboutClick = (e: any) => {
    setAnchorEl(e.target);
    setOpen(true);
  };

  const handleProjectsClick = () => {
    navigate("/");
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const featuredSection = document.querySelector(".projects-preview");
      if (featuredSection) {
        const yOffset = -120; // Negative value for top margin (adjust as needed)
        const y = featuredSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };



// in Header (index.tsx) inside the piano useEffect
useEffect(() => {
  if (pathname !== "/") return;
  if (window.matchMedia("(max-width: 599px)").matches) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if ((window as any).__NAV_PIANO_SCHEDULED__) return;
  (window as any).__NAV_PIANO_SCHEDULED__ = true;

  const btns = Array.from(document.querySelectorAll<HTMLElement>(".nav-btn"));
  if (!btns.length) return;

  const baseDelay = 130;
  const pressTime = 260;
  const keyTimeouts: number[] = [];

  const startTimeout = window.setTimeout(() => {
    btns.forEach((el, i) => {
      const t1 = window.setTimeout(() => {
        el.classList.add("playing");
        const t2 = window.setTimeout(() => el.classList.remove("playing"), pressTime);
        keyTimeouts.push(t2);
      }, i * baseDelay);
      keyTimeouts.push(t1);
    });
  }, 900);

  return () => {
    // NEW: hard reset visual state so nothing is left "pressed"
    btns.forEach((el) => el.classList.remove("playing"));

    // existing clears
    window.clearTimeout(startTimeout);
    keyTimeouts.forEach((id) => window.clearTimeout(id));
  };
}, [pathname]);

  

  return (
    <Box sx={{ display: "flex" }}>
      {/* About Me Popper - Keep existing */}
      <Popper
  open={open}
  anchorEl={anchorEl}
  placement="bottom-end"
  transition
  sx={{ zIndex: 999999 }}
>
  {({ TransitionProps }) => (
    <Fade {...TransitionProps} timeout={350}>
      <Paper
        sx={{
          background: (theme: Theme) => theme.palette.primary.main,
          mt: 1.2,
          mr: -3,
          width: "450px",
          color: "#fff",
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            py: 5,
            px: 3.5,
            borderBottom: "1px solid #fff",
          }}
        >
          <Typography
            sx={{
              fontSize: { md: "40px", sm: "32px", xs: "28px" },
              fontWeight: 700,
            }}
          >
            This is me
          </Typography>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* Avatar + name block */}
            <Box
              sx={{
                width: "110px",
                height: "110px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                "& img": {
                  width: "110px",
                  height: "110px",
                  aspectRatio: "1 / 1",   // reserve square box immediately
                  display: "block",        // prevent baseline gap/layout shift
                  objectFit: "cover",
                  borderRadius: "50%",
                  border: "5px solid #ADFF00",
                  cursor: "pointer",
                  animation: "rotation 8s infinite linear",
                  animationPlayState: "paused",
                  "&:hover": { animationPlayState: "running" },
                  "@keyframes rotation": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                },
              }}
            >
              {/* Intrinsic attrs stop the “squish” while styles load */}
              <img
                src={Me}
                alt="no content"
                width={110}
                height={110}
                loading="eager"
                decoding="async"
              />

              {/* Bigger name */}
              <Typography
                sx={{
                  fontSize: "20px", // increased
                  fontFamily: "Mohave",
                  color: "#ADFF00",
                  fontWeight: 700,
                  fontStyle: "italic",
                  mt: 1,
                  letterSpacing: "0.3px",
                }}
              >
                ADI
              </Typography>

              {/* Bigger dates */}
              <Typography
                sx={{
                  fontSize: "18px", // increased
                  fontFamily: "Mohave",
                  color: "#ADFF00",
                  fontWeight: 700,
                  fontStyle: "italic",
                }}
              >
                1998 - PRESENT
              </Typography>
            </Box>

            {/* Line / wave block */}
            <Box
              sx={{
                position: "relative",
                pl: 0.5,
                height: "110px",
                width: "250px",
              }}
            >
              <Box sx={{ position: "absolute", top: 0 }}>
                <AboutLine />
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  background: (theme: Theme) => theme.palette.primary.main,
                  width: "150px",
                  height: "70px",
                  animation: "aboutLine 1.5s forwards",
                  "@keyframes aboutLine": {
                    from: { transform: "translate(0,0)" },
                    to: { transform: "translate(150px,0)" },
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { md: "22px", xs: "16px" },
                    fontWeight: 700,
                  }}
                >
                  Hey there!
                </Typography>
                <img src={Wave} alt="no wave" style={{ marginTop: -1 }} />
              </Box>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: { md: "40px", sm: "32px", xs: "28px" },
              fontWeight: 700,
              mt: 10,
            }}
          >
            A bit more...
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              maxWidth: 350,
              fontSize: { md: "24px", xs: "16px" },
              fontWeight: 600,
            }}
          >
            builder, dreamer, & stubbornly curious
          </Typography>
        </Box>

        {/* MBTI row */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            sx={{
              fontSize: { lg: "20px", xs: "16px" },
              p: 3.5,
              fontWeight: 700,
            }}
          >
            My MBTI Personality Type
          </Typography>

          <Box
            sx={{
              mt: { md: 0, xs: 5 },
              ml: { sm: 3.5, xs: 1 },
              display: "flex",
            }}
          >
            <Button
              variant="contained"
              component="a"
              href="https://www.16personalities.com/intj-personality"
              target="_blank"
              rel="noreferrer"
              sx={{
                position: "relative",   // needed for :before box
                px: 1.5,
                py: 1.25,
                borderRadius: 0,
                transition: "0.2s",
                zIndex: 0,
                background: "#ADFF00",
                fontWeight: 700,
                fontSize: "16px",
                color: "#000",
                textDecoration: "none",
                "& *": { textDecoration: "none", color: "#000" },
                "&:hover": { background: "#ADFF00" },
                "&:before": {
                  content: "''",
                  pointerEvents: "none", // don’t block clicks at edges
                  width: "100%",
                  height: "100%",
                  background: "transparent",
                  border: "2px solid #ADFF00",
                  top: 4,
                  left: 4,
                  zIndex: -1,
                  transition: "0.2s",
                  position: "absolute",
                },
                "&:active": {
                  transition: "0.2s",
                  "&:before": { transform: "translate(-4px,-4px)", zIndex: -1 },
                  transform: "translate(4px,4px)",
                  zIndex: 0,
                },
              }}
            >
              INTJ
            </Button>
          </Box>
        </Box>
      </Paper>
    </Fade>
  )}
</Popper>


      <CssBaseline />
      
      {/* Logo - Always Visible */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          left: 80,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          cursor: "pointer",
          width: "80px",
          height: "73px",
        }}
        onClick={() => {
          navigate("/");
          // Scroll to top after navigation
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }}
      >
        <img 
          src={LogoSvg} 
          alt="Logo" 
          style={{
            width: "80px",
            height: "73px",
            filter: "brightness(0.7)",
            display: "block",
          }}
        />
      </Box>
      
      {/* Brutalist Navigation Bar */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: { xs: "none", sm: "flex" },
          gap: 0,
        }}
      >
        {/* AI PRODUCTS — highlighted as primary CTA */}
        <Box
          className="nav-btn"
          onClick={() => navigate("/ai-built")}
          sx={{
            background: "#ADFF00",
            color: "#000",
            border: "4px solid #000",
            padding: "15px 25px",
            fontSize: "18px",
            fontWeight: 900,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "4px 4px 0 #000",
            "&.playing, &:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          AI + DESIGN
        </Box>

        <Box
          className="nav-btn"
          onClick={handleProjectsClick}
          sx={{
            background: "#fff",
            color: "#000",
            border: "4px solid #000",
            padding: "15px 25px",
            fontSize: "18px",
            fontWeight: 900,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "4px 4px 0 #000",
            marginLeft: "-4px",
            "&.playing, &:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          WORK
        </Box>

        {/* PLAY nav button — disabled, uncomment to restore
        <Box
          className="nav-btn"
          onClick={() => navigate("/play")}
          sx={{
            background: "#fff",
            color: "#000",
            border: "4px solid #000",
            padding: "15px 25px",
            fontSize: "18px",
            fontWeight: 900,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "4px 4px 0 #000",
            marginLeft: "-4px",
            "&.playing, &:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          PLAY
        </Box>
        */}

        <Box
          className="nav-btn"
          component="a"
          href="https://drive.google.com/file/d/1uey6eim913OzVDYrU2rozeVqnVs5FOhd/view?usp=drivesdk"
          target="_blank"
          rel="noreferrer"
          sx={{
            background: "#fff",
            border: "4px solid #000",
            padding: "15px 25px",
            fontSize: "18px",
            fontWeight: 900,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "4px 4px 0 #000",
            marginLeft: "-4px",
            textDecoration: "none",
            color: "#000",
            display: "block",
            "&.playing, &:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          RESUME
        </Box>

        <Box
          className="nav-btn"
          component="a"
          href="https://www.linkedin.com/in/adi2612/"
          target="_blank"
          rel="noreferrer"
          sx={{
            background: "#fff",
            border: "4px solid #000",
            padding: "15px 25px",
            fontSize: "18px",
            fontWeight: 900,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "4px 4px 0 #000",
            marginLeft: "-4px",
            textDecoration: "none",
            color: "#000",
            display: "block",
            "&.playing, &:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          LINKEDIN
        </Box>

        <Box
          className="nav-btn"
          onClick={handleAboutClick}
          sx={{
            background: "#fff",
            color: "#000",
            border: "4px solid #000",
            padding: "15px 25px",
            fontSize: "18px",
            fontWeight: 900,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "4px 4px 0 #000",
            marginLeft: "-4px",
            "&.playing, &:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          ABOUT
        </Box>
      </Box>

      {/* Mobile Menu Button */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: { xs: "block", sm: "none" },
        }}
      >
        <IconButton
          onClick={handleRightDrawerToggle}
          sx={{
            background: "#fff",
            border: "4px solid #000",
            borderRadius: 0,
            width: "60px",
            height: "60px",
            boxShadow: "4px 4px 0 #000",
            color: "#0021f5",
            transition: "all 0.2s ease",
            "&.playing, &:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          <MenuIcon fontSize="large" />
        </IconButton>
      </Box>

      {/* Side Drawer */}
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          "& ::-webkit-scrollbar": {
            width: 0,
            height: "2px",
          },
          flexShrink: { sm: 0 },
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          <SideComponent />
        </Drawer>

        {/* Mobile Right Drawer */}
        <Drawer
          anchor="right"
          variant="temporary"
          open={mobileRightOpen}
          onClose={handleRightDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: "100%",
              height: "100vh",
              background: "#fff",
            },
          }}
        >
          <Box sx={{ position: "absolute", right: 20, top: 20 }}>
            <IconButton
              onClick={handleRightDrawerToggle}
              sx={{
                background: "#fff",
                border: "4px solid #000",
                borderRadius: 0,
                width: "50px",
                height: "50px",
                boxShadow: "4px 4px 0 #000",
                color: "#000",
                "&:hover": {
                  background: "#ADFF00",
                },
              }}
            >
              <Close fontSize="large" />
            </IconButton>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* AI PRODUCTS — highlighted as primary CTA */}
            <Box
              onClick={() => {
                navigate("/ai-built");
                handleRightDrawerToggle();
              }}
              sx={{
                background: "#ADFF00",
                border: "4px solid #000",
                padding: "20px 40px",
                fontSize: "24px",
                fontWeight: 900,
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "4px 4px 0 #000",
                "&:active": {
                  transform: "translate(2px, 2px)",
                  boxShadow: "2px 2px 0 #000",
                },
              }}
            >
              AI + DESIGN
            </Box>
            <Box
              onClick={() => {
                navigate("/");
                handleRightDrawerToggle();
              }}
              sx={{
                background: "#fff",
                border: "4px solid #000",
                padding: "20px 40px",
                fontSize: "24px",
                fontWeight: 900,
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "4px 4px 0 #000",
                "&:active": {
                  transform: "translate(2px, 2px)",
                  boxShadow: "2px 2px 0 #000",
                },
              }}
            >
              WORK
            </Box>
            {/* PLAY mobile nav button — disabled, uncomment to restore
            <Box
              onClick={() => {
                navigate("/play");
                handleRightDrawerToggle();
              }}
              sx={{
                background: "#fff",
                border: "4px solid #000",
                padding: "20px 40px",
                fontSize: "24px",
                fontWeight: 900,
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "4px 4px 0 #000",
                "&:active": {
                  transform: "translate(2px, 2px)",
                  boxShadow: "2px 2px 0 #000",
                },
              }}
            >
              PLAY
            </Box>
            */}
            <Box
              component="a"
              href="https://drive.google.com/file/d/1uey6eim913OzVDYrU2rozeVqnVs5FOhd/view?usp=drivesdk"
              target="_blank"
              rel="noreferrer"
              onClick={handleRightDrawerToggle}
              sx={{
                background: "#fff",
                border: "4px solid #000",
                padding: "20px 40px",
                fontSize: "24px",
                fontWeight: 900,
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "4px 4px 0 #000",
                textDecoration: "none",
                color: "#000",
                "&:active": {
                  transform: "translate(2px, 2px)",
                  boxShadow: "2px 2px 0 #000",
                },
              }}
            >
              RESUME
            </Box>
            <Box
              component="a"
              href="https://www.linkedin.com/in/adi2612/"
              target="_blank"
              rel="noreferrer"
              onClick={handleRightDrawerToggle}
              sx={{
                background: "#fff",
                border: "4px solid #000",
                padding: "20px 40px",
                fontSize: "24px",
                fontWeight: 900,
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "4px 4px 0 #000",
                textDecoration: "none",
                color: "#000",
                "&:active": {
                  transform: "translate(2px, 2px)",
                  boxShadow: "2px 2px 0 #000",
                },
              }}
            >
              LINKEDIN
            </Box>
            <Box
              onClick={() => {
                handleAboutClick({ target: null });
                handleRightDrawerToggle();
              }}
              sx={{
                background: "#fff",
                border: "4px solid #000",
                padding: "20px 40px",
                fontSize: "24px",
                fontWeight: 900,
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "4px 4px 0 #000",
                "&:active": {
                  transform: "translate(2px, 2px)",
                  boxShadow: "2px 2px 0 #000",
                },
              }}
            >
              ABOUT
            </Box>
          </Box>
        </Drawer>

        {/* Permanent Side Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              background: "transparent",
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: (theme) =>
              pathname === "/" || pathname === "/play" || pathname === "/ai-built"
                ? "none"
                : `1.5px solid ${theme.palette.primary.main}`,
            },
          }}
          open
        >
          <SideComponent />
        </Drawer>
      </Box>

      {/* Mobile Drawer Toggle */}
      {pathname !== "/" && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: mobileOpen ? 67 : 2,
            width: "40px",
            height: "40px",
            borderRadius: "100%",
            background: "#000",
            display: { sm: "none", xs: "flex" },
            justifyContent: "center",
            transition: "0.2s",
            alignItems: "center",
            opacity: mobileOpen ? 1 : 0.2,
            zIndex: 9999,
            transform: mobileOpen ? "rotate(180deg)" : "rotate(0deg)",
            color: (theme: Theme) => theme.palette.secondary.main,
          }}
          onClick={handleDrawerToggle}
        >
          <East />
        </Box>
      )}
    </Box>
  );
};

export default Header;